/**
 * Live draw dimensions + typed length parsing (Review Phase 3).
 */

import {
  formatAreaSqFt,
  formatFeetInches,
  parseArchitecturalDimension,
  pdfAreaToRealSqFeet,
  pdfPointsToRealFeet,
  realInchesPerPdfPoint,
} from './drawingMeasurements.js';
import { canvasToPdfPoint, pdfPointToCanvas } from './userMarkup.js';

function orthoLeg(origin, pt) {
  return Math.abs(pt.x - origin.x) >= Math.abs(pt.y - origin.y) ? 'horizontal' : 'vertical';
}

export function realFeetToPdfPoints(feet, ratio) {
  const inchesPerPt = realInchesPerPdfPoint(ratio);
  if (!inchesPerPt) return null;
  return (feet * 12) / inchesPerPt;
}

function parseScalarToPdfPoints(text, scaleRatio) {
  const raw = String(text || '').trim();
  if (!raw) return null;
  const arch = parseArchitecturalDimension(raw);
  if (arch?.kind === 'length' && arch.feet != null) {
    if (scaleRatio) return realFeetToPdfPoints(arch.feet, scaleRatio);
    return arch.feet * 12 * 72;
  }
  const n = Number(raw);
  if (Number.isFinite(n)) {
    if (scaleRatio) return realFeetToPdfPoints(n, scaleRatio);
    return n;
  }
  return null;
}

/** Parse user-typed length → PDF points. Numbers default to feet when scale set, else PDF pt. */
export function parseDrawLengthToPdfPoints(text, scaleRatio) {
  const raw = String(text || '').trim();
  if (!raw) return null;

  const arch = parseArchitecturalDimension(raw);
  if (arch?.kind === 'length' && arch.feet != null) {
    if (scaleRatio) return realFeetToPdfPoints(arch.feet, scaleRatio);
    return arch.feet * 12 * 72;
  }

  const plain = Number(raw);
  if (Number.isFinite(plain) && plain > 0) {
    if (scaleRatio) return realFeetToPdfPoints(plain, scaleRatio);
    return plain;
  }

  return null;
}

export function canvasSegmentLengthPdf(a, b, viewport) {
  if (!viewport || !a || !b) return 0;
  const [x0, y0] = canvasToPdfPoint(a.x, a.y, viewport);
  const [x1, y1] = canvasToPdfPoint(b.x, b.y, viewport);
  return Math.hypot(x1 - x0, y1 - y0);
}

export function formatLiveLength(pdfLength, scaleRatio, { precision = 1 } = {}) {
  if (!pdfLength || pdfLength <= 0) return '';
  if (scaleRatio) {
    const ft = pdfPointsToRealFeet(pdfLength, scaleRatio);
    return formatFeetInches(ft, { precision });
  }
  return `${Math.round(pdfLength)} pt`;
}

export function formatLiveArea(pdfArea, scaleRatio) {
  if (!pdfArea || pdfArea <= 0) return '';
  if (scaleRatio) {
    const sqFt = pdfAreaToRealSqFeet(pdfArea, scaleRatio);
    if (sqFt != null) return formatAreaSqFt(sqFt);
  }
  return `${Math.round(pdfArea)} sq pt`;
}

/** Parse user-typed rotation angle (degrees). Accepts `45`, `45°`, `-12.5`. */
export function parseRotateAngleDegrees(text) {
  const raw = String(text || '').trim().replace(/°/g, '').replace(/\s*deg(?:rees)?\.?\s*$/i, '').trim();
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * Signed rotation from typed magnitude, using mouse side relative to start ray.
 * Positive = clockwise (matches canvas drag / rotateComponent convention).
 */
export function resolveRotateDegreesFromInput({
  dimensionInput,
  startAngleRad,
  pivotCanvas,
  mouseCanvas,
  shiftSnap = false,
}) {
  const typed = parseRotateAngleDegrees(dimensionInput);
  if (typed == null || !String(dimensionInput || '').trim()) return null;

  let sign = 1;
  if (mouseCanvas && pivotCanvas && startAngleRad != null) {
    const currAngle = Math.atan2(
      mouseCanvas.y - pivotCanvas.y,
      mouseCanvas.x - pivotCanvas.x,
    );
    const deltaRad = currAngle - startAngleRad;
    if (Math.abs(deltaRad) > 1e-6) sign = deltaRad >= 0 ? 1 : -1;
  }

  let degrees = sign * Math.abs(typed);
  if (shiftSnap) {
    degrees = Math.round(degrees / 15) * 15;
  }
  return degrees;
}

/** Place canvas point at PDF distance from origin along direction to target. */
export function pointAtPdfDistance(originCanvas, targetCanvas, distancePdf, viewport, {
  ortho = false,
  orthoAxis = null,
} = {}) {
  if (!originCanvas || !viewport || !distancePdf) return null;
  let angle;
  if (targetCanvas) {
    if (ortho) {
      const dx = targetCanvas.x - originCanvas.x;
      const dy = targetCanvas.y - originCanvas.y;
      const leg = orthoAxis || orthoLeg(originCanvas, targetCanvas);
      if (leg === 'horizontal') {
        angle = dx >= 0 ? 0 : Math.PI;
      } else {
        angle = dy >= 0 ? Math.PI / 2 : -Math.PI / 2;
      }
    } else {
      angle = Math.atan2(
        targetCanvas.y - originCanvas.y,
        targetCanvas.x - originCanvas.x,
      );
    }
  } else {
    angle = 0;
  }

  const [ox, oy] = canvasToPdfPoint(originCanvas.x, originCanvas.y, viewport);
  const px = ox + distancePdf * Math.cos(angle);
  const py = oy + distancePdf * Math.sin(angle);
  return pdfPointToCanvas(px, py, viewport);
}

export function paintSegmentDimension(ctx, a, b, label) {
  if (!ctx || !a || !b || !label) return;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  if (len < 8) return;

  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  const nx = -dy / len;
  const ny = dx / len;
  const offset = 14;
  const lx = mx + nx * offset;
  const ly = my + ny * offset;

  ctx.save();
  ctx.setLineDash([]);
  ctx.strokeStyle = 'rgba(30, 64, 175, 0.55)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(a.x + nx * 8, a.y + ny * 8);
  ctx.lineTo(b.x + nx * 8, b.y + ny * 8);
  ctx.stroke();

  ctx.font = '600 11px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const pad = 4;
  const tw = ctx.measureText(label).width;
  ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
  ctx.fillRect(lx - tw / 2 - pad, ly - 8, tw + pad * 2, 16);
  ctx.fillStyle = '#1e40af';
  ctx.fillText(label, lx, ly);
  ctx.restore();
}

export function paintRectDimensions(ctx, x, y, w, h, wLabel, hLabel) {
  if (!ctx || w < 4 || h < 4) return;
  paintSegmentDimension(ctx, { x, y: y + h / 2 }, { x: x + w, y: y + h / 2 }, wLabel);
  paintSegmentDimension(ctx, { x: x + w / 2, y }, { x: x + w / 2, y: y + h }, hLabel);
}
